import React, { useState } from 'react';
import { Shield, Truck, Briefcase, FileText, Wrench, ExternalLink, CheckCircle2, HelpCircle } from 'lucide-react';

type InsuranceType = 'general-liability' | 'workers-comp' | 'commercial-auto' | 'professional-liability' | 'tools-equipment';

interface Provider {
  name: string;
  description: string;
  features?: string[];
}

export const Marketplace: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InsuranceType>('general-liability');

  const insuranceData: Record<InsuranceType, { title: string; icon: React.ReactNode; providers: Provider[] }> = {
    'general-liability': {
      title: 'General Liability Insurance',
      icon: <Shield className="w-5 h-5" />,
      providers: [
        {
          name: 'Next Insurance',
          description: 'Tailored for trades; instant quotes, certificates, strong API integration.',
          features: ['Instant online quotes', 'Digital certificates', 'Starting at $27/month'],
        },
        {
          name: 'Thimble',
          description: 'Flexible monthly or on-demand coverage for solo contractors.',
          features: ['Pay-per-job options', 'Cancel anytime', 'Same-day coverage'],
        },
        {
          name: 'Hiscox',
          description: 'Reputable national carrier; small business focused.',
          features: ['$1M-$2M coverage', 'Bundle discounts', 'AM Best A+ rated'],
        },
        {
          name: 'Nationwide',
          description: 'Established carrier with contractor programs.',
          features: ['Multi-policy discounts', '24/7 claims support', 'Nationwide coverage'],
        },
        {
          name: 'The Hartford',
          description: 'Deep construction expertise; trusted by GCs.',
          features: ['Industry specialist', 'Risk management tools', 'Fast claims processing'],
        },
      ],
    },
    'workers-comp': {
      title: "Workers' Compensation Insurance",
      icon: <Briefcase className="w-5 h-5" />,
      providers: [
        {
          name: 'Pie Insurance',
          description: 'Small-business specialist; fast online sign-up.',
          features: ['Digital-first platform', 'Fair pricing', 'Quick approval'],
        },
        {
          name: 'Cerity',
          description: 'Owned by Employers Holdings; API-ready quotes.',
          features: ['Tech-enabled quotes', 'Pay-as-you-go', 'State-specific coverage'],
        },
        {
          name: 'Next Insurance',
          description: 'Bundles with liability for easy compliance.',
          features: ['Bundle savings', 'Online portal', 'Certificate generation'],
        },
        {
          name: 'The Hartford',
          description: 'Nationwide coverage with payroll integration.',
          features: ['Payroll integration', 'Safety programs', 'Expert guidance'],
        },
        {
          name: 'State Fund',
          description: 'Public programs in CA, NY, WA, etc.',
          features: ['State-backed coverage', 'Competitive rates', 'Required in some states'],
        },
      ],
    },
    'commercial-auto': {
      title: 'Commercial Auto Insurance',
      icon: <Truck className="w-5 h-5" />,
      providers: [
        {
          name: 'Progressive Commercial',
          description: 'Market leader in contractor fleet coverage.',
          features: ['Fleet discounts', 'Roadside assistance', 'Name Your Price tool'],
        },
        {
          name: 'Geico Commercial',
          description: 'Affordable plans for sole proprietors.',
          features: ['Low rates', 'Easy online quotes', 'Mobile app'],
        },
        {
          name: 'Nationwide',
          description: 'Bundles with liability and tool coverage.',
          features: ['Multi-policy savings', 'Accident forgiveness', 'Vanishing deductible'],
        },
        {
          name: 'Travelers',
          description: 'Excellent for multi-vehicle and regional fleets.',
          features: ['Fleet management', 'Custom coverage', 'Risk control services'],
        },
        {
          name: 'The Hartford',
          description: 'Strong truck and van coverage for trades.',
          features: ['Equipment coverage', 'Hired auto', 'Non-owned auto'],
        },
      ],
    },
    'professional-liability': {
      title: 'Professional Liability (Errors & Omissions)',
      icon: <FileText className="w-5 h-5" />,
      providers: [
        {
          name: 'Hiscox',
          description: 'Ideal for small trades with consulting/design risk.',
          features: ['Tailored coverage', 'Online management', 'Legal defense included'],
        },
        {
          name: 'Chubb',
          description: 'Premium option for higher exposure or large contracts.',
          features: ['High limits available', 'Global coverage', 'Reputation protection'],
        },
        {
          name: 'Berkshire Hathaway Guard',
          description: 'Affordable small-business E&O coverage.',
          features: ['Competitive pricing', 'Quick quotes', 'Flexible terms'],
        },
        {
          name: 'Liberty Mutual',
          description: 'Bundled E&O and general liability.',
          features: ['Package discounts', 'Risk advisory', 'Claims support'],
        },
        {
          name: 'Next Insurance',
          description: 'Combined GL + E&O policies online.',
          features: ['Simple bundles', 'Digital platform', 'Fast approval'],
        },
      ],
    },
    'tools-equipment': {
      title: 'Tools & Equipment Coverage',
      icon: <Wrench className="w-5 h-5" />,
      providers: [
        {
          name: 'CoverWallet (Aon)',
          description: 'Marketplace aggregator with equipment riders.',
          features: ['Compare multiple quotes', 'Equipment scheduling', 'Theft protection'],
        },
        {
          name: 'Next Insurance',
          description: 'Add-ons for tools, gear, and job-site theft.',
          features: ['Add to existing policy', 'Replacement cost', 'Off-premises coverage'],
        },
        {
          name: 'Nationwide',
          description: 'Tailored protection for construction trades.',
          features: ['Scheduled items', 'Blanket coverage', 'Transit coverage'],
        },
        {
          name: 'Travelers',
          description: 'Comprehensive "Contractors Equipment" policies.',
          features: ['Owned & rented equipment', 'Breakdown coverage', 'Worldwide protection'],
        },
        {
          name: 'State Farm',
          description: 'Simple small-tool protection for owner-operators.',
          features: ['Easy claims', 'Local agents', 'Bundle savings'],
        },
      ],
    },
  };

  const tabs: { id: InsuranceType; label: string }[] = [
    { id: 'general-liability', label: 'General Liability' },
    { id: 'workers-comp', label: "Workers' Compensation" },
    { id: 'commercial-auto', label: 'Commercial Auto' },
    { id: 'professional-liability', label: 'Professional Liability' },
    { id: 'tools-equipment', label: 'Tools & Equipment' },
  ];

  const currentInsurance = insuranceData[activeTab];

  return (
    <div className="space-y-6">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-2xl p-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Insurance Marketplace</h1>
            <p className="text-slate-300">Compare and connect with trusted insurance providers</p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-300 rounded-xl p-4 shadow-md">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-blue-900">API-Verified Providers</h3>
            <p className="text-sm text-blue-700 mt-1">
              All providers are pre-vetted and integrated for instant quotes and certificate generation
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-lg">
        <div className="border-b border-slate-200 overflow-x-auto">
          <div className="flex min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              {currentInsurance.icon}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{currentInsurance.title}</h2>
              <p className="text-sm text-slate-600">
                {currentInsurance.providers.length} recommended providers
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {currentInsurance.providers.map((provider, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300 overflow-hidden group hover:scale-[1.02]"
              >
                <div className="p-6 pb-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0 group-hover:border-blue-200 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <Shield className="w-6 h-6 text-slate-600 group-hover:text-blue-600 transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-blue-600 hover:text-blue-700 cursor-pointer transition-colors">
                          {provider.name}
                        </h3>
                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                          {provider.description}
                        </p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600 flex-shrink-0 ml-2 transition-colors" />
                  </div>

                  {provider.features && (
                    <div className="space-y-2.5 mb-6">
                      {provider.features.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mr-2.5 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="px-6 pb-6">
                  <button className="w-full px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg">
                    Get Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white rounded-xl p-6 overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"></div>
          <div className="relative">
            <div className="flex items-start space-x-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Not sure what you need?</h3>
                <p className="text-sm text-slate-300 mt-1">
                  Take our 60-second coverage quiz to get personalized recommendations
                </p>
              </div>
            </div>
            <button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg">
              Start Coverage Quiz
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Why VendorBoom Marketplace?</h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-slate-900">Pre-Vetted Providers</div>
                <div className="text-sm text-slate-600">Industry-specific coverage options</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-slate-900">Instant Quotes</div>
                <div className="text-sm text-slate-600">API-integrated for real-time pricing</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-slate-900">Certificate Management</div>
                <div className="text-sm text-slate-600">Digital COIs sent to GCs instantly</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-6 text-center shadow-sm">
        <p className="text-slate-700 mb-3 font-medium">
          Need guidance on coverage requirements for your trade?
        </p>
        <button className="text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center hover:underline">
          Learn about required insurance by trade
          <ExternalLink className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};
